// ═══════════════════════════════════════════════════════════════════════════
// implementationPlanTab.jsx — Tasks · DoD · Customization analysis
// ═══════════════════════════════════════════════════════════════════════════
// Surfaces the implementation reality from IFACES_ENHANCED:
//   · 189 tasks across all 56 interfaces
//   · 876 DoD criteria (acceptance conditions)
//   · Classification: OOTB (85) · OOTB+Config (98) · Customization (6)
//   · Total task hours: 1,396
//   · Per-task subTasks hierarchy (3-level: step → subTask → subSubTask)
//
// The stakeholder headline this tab proves visually:
//   "97% of the work is OOTB or configuration. Only 6 of 189 tasks (3%)
//    require custom code. This is a low-risk, mostly-standard implementation."
//
// Views:
//   1. 📊 Programme Dashboard  — classification breakdown, hours by phase
//   2. 🚨 Customization Watch  — the 6 custom-code tasks (risk concentration)
//   3. 📋 All Tasks            — sortable/filterable list
//   4. ⏱️ Burn-Down            — DoD completion by phase
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { T, FONTS, chipBtn, pill } from "./theme";
import {
  IFACES, GRP_META, PHASE_META, CX_META, TASK_TYPE_C,
  getAllCustomizationTasks, getClassificationSummary,
  getTotalTasks, getTotalDoDCriteria, getTotalTaskHours,
  getInterfaceProgress,
} from "./IfacesData";
import { useNavigation } from "./navigation";

const VIEWS = [
  { id: "dashboard",     label: "📊 Programme dashboard",  color: T.cyan },
  { id: "customization", label: "🚨 Customization watch", color: T.red },
  { id: "tasks",         label: "📋 All tasks",            color: T.purple },
  { id: "burndown",      label: "⏱️ Burn-down",            color: T.green },
];

export default function ImplementationPlanTab({ state }) {
  const [view, setView] = useState("dashboard");
  const nav = useNavigation();

  const summary = useMemo(() => getClassificationSummary(), []);
  const totalTasks   = getTotalTasks();
  const totalDoD     = getTotalDoDCriteria();
  const totalHours   = getTotalTaskHours();

  return (
    <div style={{
      padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto",
      fontFamily: FONTS.body,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          🏗 Implementation Plan
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          {totalTasks} tasks · {totalDoD} acceptance criteria · {totalHours} task-hours across {IFACES.length} interfaces.
          {" "}<strong style={{ color: T.green }}>
            {Math.round(((summary.ootb + summary.ootbConfig) / summary.total) * 100)}% OOTB or config
          </strong>; only <strong style={{ color: T.red }}>{summary.customization} customization tasks</strong> ({Math.round(summary.customization / summary.total * 100)}%).
        </div>
        <div style={{ fontSize: 9.5, color: T.amber, marginTop: 6, fontStyle: "italic" }}>
          "Stakeholders ask one question: how risky is this? Answer with classification math — 97% standard, 3% custom — and the conversation is over."
        </div>
      </div>

      {/* Stat strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        {[
          { l: "Tasks",            v: totalTasks,                c: T.cyan },
          { l: "DoD criteria",     v: totalDoD,                  c: T.purple },
          { l: "Total hours",      v: totalHours,                c: T.amber },
          { l: "OOTB",             v: summary.ootb,              c: T.green },
          { l: "OOTB + Config",    v: summary.ootbConfig,        c: T.cyan },
          { l: "Customization",    v: summary.customization,     c: T.red },
        ].map(k => (
          <div key={k.l} style={{
            background: T.bg2, border: `1px solid ${k.c}30`, borderRadius: 6,
            padding: "10px 16px", textAlign: "center", minWidth: 96,
          }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 7.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase", marginTop: 2 }}>{k.l}</div>
          </div>
        ))}
      </div>

      {/* View nav */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={chipBtn(view === v.id, v.color)}>
            {v.label}
          </button>
        ))}
      </div>

      {view === "dashboard"     && <DashboardView summary={summary}/>}
      {view === "customization" && <CustomizationView nav={nav}/>}
      {view === "tasks"         && <TasksView nav={nav}/>}
      {view === "burndown"      && <BurnDownView state={state}/>}
    </div>
  );
}

// ─── 1 · Programme dashboard ─────────────────────────────────────────────
function DashboardView({ summary }) {
  // Hours by phase
  const hoursByPhase = useMemo(() => {
    const m = {};
    for (const f of IFACES) {
      const p = f.phase;
      m[p] = (m[p] || 0) + (f.enhanced?.taskHours || 0);
    }
    return m;
  }, []);

  // Tasks by type
  const tasksByType = useMemo(() => {
    const m = {};
    for (const f of IFACES) for (const t of (f.tasks || [])) {
      m[t.type] = (m[t.type] || 0) + 1;
    }
    return m;
  }, []);

  const ootbPct = (summary.ootb / summary.total) * 100;
  const configPct = (summary.ootbConfig / summary.total) * 100;
  const customPct = (summary.customization / summary.total) * 100;

  return (
    <div>
      {/* Big classification doughnut */}
      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
        padding: "16px 20px", marginBottom: 14,
      }}>
        <div style={{ fontSize: 11, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
          🎯 IMPLEMENTATION CLASSIFICATION
        </div>

        {/* The killer chart: stacked bar showing the proportion */}
        <div style={{ display: "flex", height: 60, borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
          <div style={{
            width: `${ootbPct}%`, background: `linear-gradient(135deg, ${T.green}, ${T.teal})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: FONTS.header, fontSize: 22, fontWeight: 700, color: "#000",
          }}>
            {summary.ootb}
          </div>
          <div style={{
            width: `${configPct}%`, background: `linear-gradient(135deg, ${T.cyan}, ${T.blue})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: FONTS.header, fontSize: 22, fontWeight: 700, color: "#000",
          }}>
            {summary.ootbConfig}
          </div>
          <div style={{
            width: `${customPct}%`, minWidth: 50,
            background: `linear-gradient(135deg, ${T.red}, ${T.pink})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: FONTS.header, fontSize: 22, fontWeight: 700, color: "#fff",
          }}>
            {summary.customization}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.text2 }}>
          <div>
            <span style={{ color: T.green, fontWeight: 700 }}>OOTB ({ootbPct.toFixed(0)}%)</span> — install, click, done. Zero custom code.
          </div>
          <div>
            <span style={{ color: T.cyan, fontWeight: 700 }}>OOTB+Config ({configPct.toFixed(0)}%)</span> — supplied configuration surfaces, no code.
          </div>
          <div>
            <span style={{ color: T.red, fontWeight: 700 }}>Customization ({customPct.toFixed(0)}%)</span> — bespoke code/extensions.
          </div>
        </div>
      </div>

      {/* Hours by phase */}
      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
        padding: "16px 20px", marginBottom: 14,
      }}>
        <div style={{ fontSize: 11, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
          ⏱️ TASK HOURS BY PHASE
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 180 }}>
          {Object.entries(PHASE_META).map(([p, meta]) => {
            const h = hoursByPhase[p] || 0;
            const maxH = Math.max(...Object.values(hoursByPhase));
            const heightPct = maxH > 0 ? (h / maxH) * 100 : 0;
            return (
              <div key={p} style={{ flex: 1, textAlign: "center" }}>
                <div style={{
                  height: `${heightPct}%`,
                  background: `linear-gradient(180deg, ${meta.c}, ${meta.c}40)`,
                  borderRadius: "4px 4px 0 0",
                  position: "relative",
                  minHeight: 4,
                }}>
                  <span style={{
                    position: "absolute", top: -18, left: 0, right: 0,
                    fontSize: 10, color: meta.c, fontFamily: FONTS.mono, fontWeight: 700,
                  }}>{h}h</span>
                </div>
                <div style={{ fontSize: 9, color: meta.c, fontFamily: FONTS.mono, fontWeight: 700, marginTop: 4 }}>
                  P{p}
                </div>
                <div style={{ fontSize: 7.5, color: T.text3, marginTop: 1,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {meta.duration}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Task types breakdown */}
      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
        padding: "16px 20px",
      }}>
        <div style={{ fontSize: 11, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
          🛠️ TASK TYPES BREAKDOWN
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
          {Object.entries(tasksByType).sort((a, b) => b[1] - a[1]).map(([type, count]) => {
            const c = TASK_TYPE_C[type] || T.steel;
            return (
              <div key={type} style={{
                background: T.bg3, border: `1px solid ${c}30`,
                borderLeft: `3px solid ${c}`,
                padding: "8px 10px", borderRadius: 4,
              }}>
                <div style={{ fontSize: 10, color: c, fontWeight: 700, marginBottom: 2 }}>{type}</div>
                <div style={{ fontSize: 16, color: T.text, fontFamily: FONTS.mono, fontWeight: 700 }}>{count}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── 2 · Customization watch — the 6 custom tasks ───────────────────────
function CustomizationView({ nav }) {
  const customs = useMemo(() => getAllCustomizationTasks(), []);

  return (
    <div>
      <div style={{
        padding: "14px 18px", marginBottom: 16,
        background: `${T.red}10`, border: `1px solid ${T.red}40`, borderLeft: `4px solid ${T.red}`,
        borderRadius: 6,
      }}>
        <div style={{ fontSize: 11, color: T.red, fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>
          🚨 RISK CONCENTRATION · {customs.length} CUSTOMIZATION TASKS
        </div>
        <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.55 }}>
          These are the only tasks requiring bespoke code/extensions across the entire programme.
          Each one is a risk concentration point: longer to develop, harder to test, expensive to maintain,
          and the most likely source of post-go-live incidents. <strong style={{ color: T.text }}>
          Track these as if they were the whole project.</strong>
        </div>
      </div>

      {customs.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: T.text3, fontStyle: "italic" }}>
          No customization tasks — pure OOTB/Config programme. (Verify against IFACES_ENHANCED.)
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
          {customs.map(t => {
            const c = GRP_META[t.grp]?.color || T.steel;
            return (
              <div key={`${t.interfaceId}-${t.id}`} style={{
                background: T.bg2,
                border: `1px solid ${T.red}40`,
                borderLeft: `4px solid ${T.red}`,
                borderRadius: 6, padding: "12px 16px",
              }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span style={{ fontFamily: FONTS.header, fontSize: 22, color: T.red, opacity: 0.7, minWidth: 40 }}>
                    {t.hours}h
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: c, fontWeight: 700,
                        cursor: "pointer" }}
                        onClick={() => nav?.goToInterface(t.interfaceId)}>
                        {t.interfaceId} →
                      </span>
                      <span style={pill(T.red)}>Customization</span>
                      <span style={pill(CX_META[t.cx]?.c || T.steel)}>CX {t.cx} · {CX_META[t.cx]?.l}</span>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 4 }}>
                      {t.name}
                    </div>
                    <div style={{ fontSize: 9.5, color: T.text2, fontStyle: "italic", marginBottom: 6 }}>
                      Interface: {t.interfaceName}
                    </div>
                    {t.classificationRationale && (
                      <div style={{
                        background: `${T.red}10`, border: `1px solid ${T.red}30`,
                        padding: "6px 10px", borderRadius: 4, marginBottom: 6,
                      }}>
                        <div style={{ fontSize: 8, color: T.red, fontWeight: 700, letterSpacing: 1, marginBottom: 3 }}>
                          WHY IT'S CUSTOM
                        </div>
                        <div style={{ fontSize: 9.5, color: T.text }}>{t.classificationRationale}</div>
                      </div>
                    )}
                    {t.dod && t.dod.length > 0 && (
                      <div>
                        <div style={{ fontSize: 8, color: T.text3, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
                          ACCEPTANCE CRITERIA ({t.dod.length})
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 9, color: T.text2 }}>
                          {t.dod.slice(0, 5).map((d, i) => <li key={i}>{d}</li>)}
                          {t.dod.length > 5 && <li style={{ color: T.text3, fontStyle: "italic" }}>+{t.dod.length - 5} more…</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── 3 · All tasks · sortable / filterable ──────────────────────────────
function TasksView({ nav }) {
  const [filterType, setFilterType] = useState("ALL");
  const [filterImpl, setFilterImpl] = useState("ALL");
  const [sort, setSort]             = useState("hours-desc");

  const tasks = useMemo(() => {
    return IFACES.flatMap(f => (f.tasks || []).map(t => ({
      ...t,
      interfaceId: f.id,
      interfaceName: f.name,
      grp: f.grp,
      phase: f.phase,
    })));
  }, []);

  const filtered = useMemo(() => {
    let arr = tasks;
    if (filterType !== "ALL") arr = arr.filter(t => t.type === filterType);
    if (filterImpl !== "ALL") arr = arr.filter(t => t.implementationType === filterImpl);
    if (sort === "hours-desc") arr = [...arr].sort((a, b) => (b.hours || 0) - (a.hours || 0));
    if (sort === "hours-asc")  arr = [...arr].sort((a, b) => (a.hours || 0) - (b.hours || 0));
    if (sort === "dod-desc")   arr = [...arr].sort((a, b) => (b.dod?.length || 0) - (a.dod?.length || 0));
    return arr;
  }, [tasks, filterType, filterImpl, sort]);

  const types = useMemo(() => [...new Set(tasks.map(t => t.type))].sort(), [tasks]);

  return (
    <div>
      <div style={{
        display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap",
        padding: "10px 14px", background: T.bg2, borderRadius: 6, border: `1px solid ${T.border}`,
      }}>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ background: T.bg3, border: `1px solid ${T.border}`, color: T.text, padding: "5px 8px", borderRadius: 4, fontSize: 10 }}>
          <option value="ALL">All task types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterImpl} onChange={e => setFilterImpl(e.target.value)}
          style={{ background: T.bg3, border: `1px solid ${T.border}`, color: T.text, padding: "5px 8px", borderRadius: 4, fontSize: 10 }}>
          <option value="ALL">All implementation</option>
          <option value="OOTB">OOTB</option>
          <option value="OOTB + Config">OOTB + Config</option>
          <option value="Customization">Customization</option>
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)}
          style={{ background: T.bg3, border: `1px solid ${T.border}`, color: T.text, padding: "5px 8px", borderRadius: 4, fontSize: 10 }}>
          <option value="hours-desc">Hours: most first</option>
          <option value="hours-asc">Hours: fewest first</option>
          <option value="dod-desc">DoD count: most first</option>
        </select>
        <span style={{ marginLeft: "auto", fontSize: 10, color: T.text2 }}>{filtered.length} tasks</span>
      </div>

      <div style={{ background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead>
            <tr style={{ background: T.bg3 }}>
              <th style={th}>Interface</th>
              <th style={th}>Task</th>
              <th style={th}>Type</th>
              <th style={th}>Impl.</th>
              <th style={th}>CX</th>
              <th style={th}>Hours</th>
              <th style={th}>DoD</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => {
              const grpColor = GRP_META[t.grp]?.color || T.steel;
              const typeColor = TASK_TYPE_C[t.type] || T.steel;
              const implColor = t.implementationType === "Customization" ? T.red :
                                t.implementationType === "OOTB + Config" ? T.cyan : T.green;
              return (
                <tr key={`${t.interfaceId}-${t.id}`} style={{ borderBottom: `1px solid ${T.border2}` }}>
                  <td style={td}>
                    <span onClick={() => nav?.goToInterface(t.interfaceId)}
                      style={{ color: grpColor, fontFamily: FONTS.mono, fontWeight: 700, cursor: "pointer" }}>
                      {t.interfaceId}
                    </span>
                  </td>
                  <td style={{ ...td, color: T.text }}>{t.name}</td>
                  <td style={td}><span style={pill(typeColor)}>{t.type}</span></td>
                  <td style={td}><span style={pill(implColor)}>{t.implementationType}</span></td>
                  <td style={td}><span style={pill(CX_META[t.cx]?.c || T.steel)}>CX {t.cx}</span></td>
                  <td style={{ ...td, fontFamily: FONTS.mono, color: T.amber, fontWeight: 700 }}>{t.hours}h</td>
                  <td style={{ ...td, fontFamily: FONTS.mono, color: T.purple }}>{t.dod?.length || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── 4 · Burn-down · DoD completion by phase ────────────────────────────
function BurnDownView({ state }) {
  const phaseStats = useMemo(() => {
    const stats = {};
    for (const phase of Object.keys(PHASE_META)) {
      const ifs = IFACES.filter(f => String(f.phase) === phase);
      let total = 0, done = 0;
      for (const f of ifs) {
        const p = getInterfaceProgress(f, state?.dodState || {}, state?.nisState || {});
        total += p.total;
        done  += p.done;
      }
      stats[phase] = { total, done, pct: total > 0 ? (done / total) * 100 : 0, count: ifs.length };
    }
    return stats;
  }, [state?.dodState, state?.nisState]);

  return (
    <div>
      <div style={{ padding: "10px 14px", marginBottom: 14,
        background: `${T.green}10`, border: `1px solid ${T.green}30`, borderRadius: 6,
        fontSize: 10, color: T.text2 }}>
        💡 Phase-by-phase DoD completion. The numbers update as you check off acceptance criteria in the Tracker.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
        {Object.entries(PHASE_META).map(([p, meta]) => {
          const s = phaseStats[p];
          return (
            <div key={p} style={{
              background: T.bg2,
              border: `1px solid ${meta.c}40`,
              borderLeft: `4px solid ${meta.c}`,
              borderRadius: 6, padding: "12px 16px",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div>
                  <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700, color: meta.c }}>
                    {meta.l}
                  </div>
                  <div style={{ fontSize: 9, color: T.text2 }}>
                    {meta.duration} · {s.count} interfaces · {s.description}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: FONTS.header, fontSize: 18, color: meta.c, fontWeight: 700 }}>
                    {Math.round(s.pct)}%
                  </div>
                  <div style={{ fontSize: 8, color: T.text2, fontFamily: FONTS.mono }}>
                    {s.done} / {s.total} DoD
                  </div>
                </div>
              </div>

              <div style={{ height: 8, background: T.bg3, borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: "100%", width: `${s.pct}%`,
                  background: `linear-gradient(90deg, ${meta.c}, ${meta.c}80)`,
                  transition: "width 0.4s",
                }}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Style helpers ──────────────────────────────────────────────────────
const th = {
  padding: "10px 12px", textAlign: "left", fontSize: 8, color: T.text2,
  fontFamily: FONTS.mono, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600,
  borderBottom: `1px solid ${T.border}`,
};
const td = { padding: "8px 12px", fontSize: 10, color: T.text };
